/**
 * TerraMatrix Universal Data Platform - MessageBubble Component
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 * Updated: December 2025 - Added Save as Template button
 *
 * Chat message bubble component.
 */

import React, { useState } from 'react';
import { ChatMessage } from '@/hooks/data/useDataQuery';
import { cn } from '@/lib/utils';
import { SaveTemplateModal } from './SaveTemplateModal';

interface MessageBubbleProps {
  message: ChatMessage;
  children?: React.ReactNode;
  /** Callback when a template is saved */
  onTemplateSaved?: () => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, children, onTemplateSaved }) => {
  const [showSaveModal, setShowSaveModal] = useState(false);

  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  // Only allow saving as template if the query was successful (has success: true)
  const canSaveAsTemplate = !isUser && !isSystem && !message.isError && !message.isLoading && message.success && message.templateQuery;

  return (
    <div
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start',
        isSystem && 'justify-center'
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-lg p-4',
          isUser && 'bg-primary text-primary-foreground',
          !isUser && !isSystem && !message.isError && 'bg-muted',
          message.isError && 'bg-destructive/10 border border-destructive text-destructive',
          isSystem && 'bg-accent/50 text-muted-foreground text-sm max-w-full',
          message.isLoading && 'animate-pulse'
        )}
      >
        {/* Message Content */}
        <div className="whitespace-pre-wrap">{message.content}</div>

        {/* DEBUG INFORMATION - SHOW PROMINENTLY */}
        {message.debug && message.role === 'assistant' && (
          <div className="mt-4 p-3 bg-background/80 rounded-md border border-border space-y-3">
            <div className="text-xs font-bold text-foreground/70 uppercase tracking-wide">
              🔍 QUERY EXECUTION DETAILS
            </div>

            {/* Intent */}
            {message.debug.intent && (
              <div>
                <div className="text-xs font-semibold mb-1 text-foreground/90">🎯 Intent:</div>
                <div className="text-xs bg-muted p-2 rounded font-mono text-foreground/80">
                  Type: {(message.debug.intent as any).type}<br />
                  Metrics: {(message.debug.intent as any).metrics?.join(', ') || 'none'}<br />
                  Entities: {(message.debug.intent as any).entities?.join(', ') || 'none'}<br />
                  Keywords: {(message.debug.intent as any).keywords?.slice(0, 10).join(', ') || 'none'}
                </div>
              </div>
            )}

            {/* Matched Sources */}
            {message.debug.matchedSources && message.debug.matchedSources.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-1 text-foreground/90">
                  🤖 AI Source Matching ({message.debug.matchedSources.length} sources):
                </div>
                <div className="space-y-2">
                  {message.debug.matchedSources.map((source: any, idx: number) => (
                    <div
                      key={idx}
                      className="text-xs bg-muted p-2 rounded border border-border/50"
                    >
                      <div className="font-semibold text-foreground">{source.name}</div>
                      <div className="text-foreground/70 mt-1">
                        <span className="font-medium">
                          Confidence: {Math.round((source.confidence || source.score || 0) * 100)}%
                        </span>
                        {source.canAnswerQuery !== undefined && (
                          <span className="ml-2">
                            • Can Answer: {source.canAnswerQuery ? '✅ Yes' : '❌ No'}
                          </span>
                        )}
                      </div>
                      {source.reasoning && (
                        <div className="text-foreground/60 mt-1 text-xs italic">
                          {source.reasoning}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generated Query */}
            {message.debug.generatedQuery && (
              <div>
                <div className="text-xs font-semibold mb-1 text-foreground/90">🔨 Generated Database Query:</div>
                {(message.debug.generatedQuery as any).explanation && (
                  <div className="text-xs text-foreground/70 mb-2 italic">
                    {(message.debug.generatedQuery as any).explanation}
                  </div>
                )}
                <div className="text-xs bg-muted p-2 rounded font-mono overflow-x-auto max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-foreground/80">
                    {JSON.stringify((message.debug.generatedQuery as any).query, null, 2)}
                  </pre>
                </div>
                {(message.debug.generatedQuery as any).confidence !== undefined && (
                  <div className="text-xs text-foreground/60 mt-1">
                    Query Confidence: {Math.round((message.debug.generatedQuery as any).confidence * 100)}%
                  </div>
                )}
              </div>
            )}

            {/* Execution Time */}
            {message.debug.executionTimeMs !== undefined && (
              <div className="text-xs font-medium text-foreground/70">
                ⚡ Total Execution Time: {message.debug.executionTimeMs}ms
              </div>
            )}
          </div>
        )}

        {/* Save as Template Button */}
        {canSaveAsTemplate && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <button
              onClick={() => setShowSaveModal(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium
                         rounded-md border border-primary/30 bg-primary/5
                         text-primary hover:bg-primary/10 hover:border-primary/50
                         transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save as Template
            </button>
          </div>
        )}

        {/* Children (data results, follow-up suggestions, etc.) */}
        {children}

        {/* Timestamp & Source */}
        <div className="flex items-center justify-between mt-2 text-xs opacity-60">
          <span>{message.timestamp.toLocaleTimeString()}</span>
          {message.source && <span>Source: {message.source.name}</span>}
        </div>
      </div>

      {/* Save Template Modal */}
      {canSaveAsTemplate && (
        <SaveTemplateModal
          open={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          templateQuery={message.templateQuery}
          templateVariables={message.templateVariables}
          onSaved={() => {
            setShowSaveModal(false);
            onTemplateSaved?.();
          }}
        />
      )}
    </div>
  );
};

export default MessageBubble;
